// Other plugins.
import { logManager } from '../logs'

// Helpers.
import { mergeValues } from '../common/utils'
import { getVersion } from '../common/version'
import { iceCollectionCheckFunction } from './utils/defaults'

// Parse and/or Validate
import { validation as v8n, parse, enums } from '../common/validation'

const log = logManager.getLogger('CALL')

/*
 * JsDoc documentation for call configs are found in the Call plugin indexes,
 *    since they are slightly different for Link vs. CPaaS.
 */
const defaultOptions = {
  // Native Peer configs.
  defaultPeerConfig: {
    sdpSemantics: 'unified-plan',
    iceServers: []
  },

  // ICE collection configs.
  iceCollectionIdealTimeout: 1000,
  iceCollectionMaxTimeout: 3000,
  serverTurnCredentials: true,

  // SDP manipulation configs.
  sdpHandlers: [],
  removeH264Codecs: true,
  removeBundling: false,

  // Other.
  earlyMedia: false,
  resyncOnConnect: false,
  mediaBrokerOnly: false,
  trickleIceMode: 'NONE',
  normalizeDestination: true,
  ringingFeedbackMode: 'auto',
  callAuditTimer: 25000,
  mediaConnectionRetryDelay: 3000
}

// config validation
const v8nValidation = v8n.schema({
  // Native Peer configs.
  defaultPeerConfig: v8n.schema({
    sdpSemantics: enums(['unified-plan', 'plan-b']),
    // Validation for currently supported properties of an RTCPeerConfiguration,
    // as specified at: https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/RTCPeerConnection
    // If more params are supported in the future, then we need to extend this validation.
    iceServers: v8n.array(),
    iceCandidatePoolSize: v8n.optional(v8n.positive()),
    iceTransportPolicy: v8n.optional(enums(['all', 'public', 'relay'])),
    bundlePolicy: v8n.optional(enums(['balanced', 'max-compat', 'max-bundle'])),
    certificates: v8n.optional(
      v8n.array().every.schema({
        expires: v8n.string()
      })
    ),
    peerIdentity: v8n.optional(v8n.string()),
    rtcpMuxPolicy: v8n.optional(enums(['negotiate', 'require']))
  }),

  // ICE collection configs.
  iceCollectionIdealTimeout: v8n.positive(),
  iceCollectionMaxTimeout: v8n.positive(),
  iceCollectionCheckFunction: v8n.optional(v8n.function()),
  serverTurnCredentials: v8n.boolean(),

  // SDP manipulation configs.
  sdpHandlers: v8n.array(),
  removeH264Codecs: v8n.boolean(),
  removeBundling: v8n.boolean(),

  // Other
  earlyMedia: v8n.boolean(),
  resyncOnConnect: v8n.boolean(),
  mediaBrokerOnly: v8n.boolean(),
  trickleIceMode: v8n.string(),
  normalizeDestination: v8n.boolean(),
  ringingFeedbackMode: enums(['auto', 'manual']),
  callAuditTimer: v8n.positive(),
  mediaConnectionRetryDelay: v8n.positive()
})
const parseOptions = parse('call', v8nValidation)

/**
 * A helper function to validate and fix STUN/TURN server urls according to RFC Spec.
 *
 * @method fixIceServerUrls
 * @param {string} url The URL of the ice server
 * @return The updated URL
 */
export function fixIceServerUrls (url) {
  let serverUrl
  try {
    serverUrl = new URL(url)
  } catch (error) {
    log.warn(`Invalid URL specified for ice server: ${url}`)
    return url
  }
  const protocol = serverUrl.protocol
  const query = serverUrl.search
  if (!query) {
    return url
  }

  let queryFilterFunc
  if (protocol && protocol.toLowerCase().startsWith('turn')) {
    // Remove all query parameters other than "transport" for TURN URLs.
    queryFilterFunc = key => key !== 'transport'
  } else {
    // Remove all query parameters for STUN URLs
    queryFilterFunc = () => true
  }
  const serverQueryParams = new URLSearchParams(query)
  Array.from(serverQueryParams.keys())
    .filter(queryFilterFunc)
    .forEach(key => serverQueryParams.delete(key))

  const newQuery = serverQueryParams.toString()
  serverUrl.search = newQuery
  if (serverUrl.search.length !== query.length) {
    log.warn(`Invalid STUN/TURN server URL provided: ${url}`)
  }

  return serverUrl.toString()
}

/**
 * Helper function to format the Call configs as the plugins expect it to be.
 * @method parseConfigs
 * @param {Object} options Call configs provided by application.
 * @returns {Object} Call configs re-formatted as needed.
 */
export default function parseConfigs (options = {}) {
  // Only parse the configs for v4+.
  const majorVersion = getVersion()[0]
  if (majorVersion < 4) {
    return options
  }

  /*
   * For backwards compatibility between doubly old ICE config and old config.
   * iceserver --> iceServers
   */
  if (options.iceserver && !options.iceServers) {
    log.warn('Call configuration `iceserver` is being replaced by `iceServers`. Please update your configuration.')
    options.iceServers = options.iceserver
    delete options.iceserver
  }

  // If user did not provide this new way of configuration, create it.
  if (!options.defaultPeerConfig) {
    options.defaultPeerConfig = {}
  }

  /*
   * For backwards-compat, update config to move sdpSemantics into defaultPeerConfig.
   * sdpSemantics --> defaultPeerConfig.sdpSemantics
   */
  if (options.sdpSemantics) {
    // .. and if new way is not present, then use old way but store it in the new config.
    if (!options.defaultPeerConfig.sdpSemantics) {
      log.warn(
        'Call configuration `sdpSemantics` has been moved to `defaultPeerConfig.sdpSemantics`. Please update your configuration.'
      )
      // save the old config item under the new way
      options.defaultPeerConfig.sdpSemantics = options.sdpSemantics
    }

    // No longer need the old config item
    delete options.sdpSemantics
  }

  /*
   * For backwards-compat, update config to move iceServers into defaultPeerConfig.
   * iceServers --> defaultPeerConfig.iceServers
   */
  if (options.iceServers) {
    if (!options.defaultPeerConfig.iceServers) {
      // save the old config item under the new way
      options.defaultPeerConfig.iceServers = options.iceServers
    }

    // No longer need the old config item
    delete options.iceServers
  }

  /*
   * For backwards-compat, make sure all iceServers use urls (not url).
   * iceServer.url --> iceServer.urls
   */
  if (options.defaultPeerConfig.iceServers) {
    options.defaultPeerConfig.iceServers.forEach(iceServer => {
      if (iceServer.url) {
        log.warn(
          'Call configuration `iceServers.url` is being replaced by `iceServers.urls`, plural. Please update your configuration.'
        )
        iceServer.urls = iceServer.url
        delete iceServer.url
      }
    })
  }

  if (options.ringingFeedbackMode && !['auto', 'manual'].includes(options.ringingFeedbackMode)) {
    log.warn('Call configuration `ringingFeedbackMode` invalid. Switching to `auto`.')
    options.ringingFeedbackMode = 'auto'
  }

  // For backwards compatiblilty, if `iceCollectionDelay` and `maxIceTimeout` were provided, and `iceCollectionIdealTimeout`
  //  and `iceCollectionMaxTimeout` were not, we will use the `iceCollectionDelay` as the ideal timeout, and `maxIceTimeout`
  //  as the max.
  if (options.iceCollectionDelay) {
    if (!options.iceCollectionIdealTimeout) {
      options.iceCollectionIdealTimeout = options.iceCollectionDelay
    }
    delete options.iceCollectionDelay
  }
  if (options.maxIceTimeout && !options.iceCollectionMaxTimeout) {
    if (!options.iceCollectionMaxTimeout) {
      options.iceCollectionMaxTimeout = options.maxIceTimeout
    }
    delete options.maxIceTimeout
  }
  return options
}

/**
 * Helper function to merge the default Call configs into the application-provided
 *   configs and validate them.
 * @param  {Object} options Call configs with expected formatting.
 * @return {Object} Call configs with defaults included.
 */
export function mergeDefaults (options = {}) {
  options = mergeValues(defaultOptions, options)
  // If no ice collection check function was provided, get the default one.
  if (!options.iceCollectionCheckFunction) {
    options.iceCollectionCheckFunction = iceCollectionCheckFunction
  }
  parseOptions(options)

  return options
}
